"use client";

import React, { useEffect, useState } from "react";

const RefundPolicyOrganism = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedItem, setExpandedItem] = useState("privacy-basics");
  const [scrollProgress, setScrollProgress] = useState(0);

  const sections = [
    {
      id: "overview",
      title: "Return & Refund Policy Overview",
      eyebrow: "Foundation",
      content: [
        "A Refund Policy defines the terms and conditions under which customers can request refunds for purchased products or services.",
        "It explains procedures, eligibility, rights, and company responsibilities in refund or return scenarios.",
        "Clear and transparent policies help build trust and reduce disputes.",
      ],
    },
    {
      id: "privacy-basics",
      title: "Related Privacy Policy Context",
      eyebrow: "Section 01",
      content: [
        "A Privacy Policy explains how a company collects, uses, stores, and protects personal information.",
        "It informs users about data rights and company data practices.",
        "Personal information may include name, email, phone number, payment details, and related account data.",
      ],
    },
    {
      id: "data-practices",
      title: "Data Collection and Use",
      eyebrow: "Section 02",
      content: [
        "Data may be collected through website forms, cookies, tracking technologies, or service interactions.",
        "Collected data may be used for order processing, customer support, communication, and service improvements.",
        "If data is shared with third parties (such as analytics or marketing providers), this should be disclosed.",
      ],
    },
    {
      id: "data-rights",
      title: "Data Security and User Rights",
      eyebrow: "Section 03",
      content: [
        "The Company should use reasonable safeguards to protect user data from unauthorized access, loss, or disclosure.",
        "Users may have rights to access, correct, delete, or restrict processing of their personal data.",
        "Consent mechanisms and opt-out options (for example, marketing communication preferences) should be provided where required.",
      ],
    },
    {
      id: "refund-eligibility",
      title: "Refund Eligibility and Timeframe",
      eyebrow: "Section 04",
      content: [
        "The policy should clearly define who is eligible for refunds and under what purchase conditions.",
        "A specific time window for submitting refund requests should be communicated.",
        "Requests submitted outside eligible conditions or timelines may be declined.",
      ],
    },
    {
      id: "refund-process",
      title: "Refund Process and Methods",
      eyebrow: "Section 05",
      content: [
        "The policy should explain the step-by-step process customers must follow to request a refund.",
        "Available refund methods (such as original payment method, store credit, or other approved channels) should be listed.",
        "Processing timelines may vary depending on payment provider and internal verification steps.",
      ],
    },
    {
      id: "exceptions-support",
      title: "Non-Refundable Items, Exceptions, and Support",
      eyebrow: "Section 06",
      content: [
        "Any non-refundable products or services must be clearly identified.",
        "Special exceptions or cancellation conditions should be transparently stated.",
        "A customer support contact or channel should be available for refund and return inquiries.",
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.35, rootMargin: "-12% 0px -45% 0px" },
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleAccordion = (id) => {
    setExpandedItem((prev) => (prev === id ? "" : id));
  };

  return (
    <div className="bg-background-primary text-text-primary font-body min-h-screen selection:bg-bg selection:text-brand">
      <div className="fixed left-0 top-0 z-50 h-[3px] w-full bg-brand/10">
        <div className="h-full bg-gradient-to-r from-brand to-accent transition-all duration-200" style={{ width: `${scrollProgress}%` }} />
      </div>

      <main className="container !py-14 !md:py-20">
        <header className="relative overflow-hidden rounded-3xl border border-border-primary bg-surface-primary !px-7 !py-10 !md:px-12 !md:py-14 shadow-lg animate-fade-up">
          <div className="relative grid !gap-7 lg:grid-cols-[1.2fr_0.85fr] lg:items-end">
            <div>
              <h1 className="!mt-5 text-3xl font-display font-extrabold leading-[1.02] tracking-tight text-ink md:text-6xl">
                Return & <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Refund Policy</span>
              </h1>
              <p className="!mt-5 !max-w-3xl text-base leading-relaxed text-ink-2 md:text-[19px]">
                This page defines refund and return expectations, including eligibility, process, methods, non-refundable scenarios, and support guidance for customers.
              </p>
            </div>
            <div className="grid !gap-3 text-sm">
              <div className="rounded-2xl border border-border-primary bg-surface-primary !p-4 shadow-sm">
                <p className="font-semibold text-ink">Eligibility Window</p>
                <p className="!mt-1 text-ink-2">Refund requests are reviewed per policy criteria and timeline.</p>
              </div>
              <div className="rounded-2xl border border-border-primary bg-surface-primary !p-4 shadow-sm">
                <p className="font-semibold text-ink">Customer Support</p>
                <p className="!mt-1 text-ink-2">Support channels remain available for refund-related assistance.</p>
              </div>
            </div>
          </div>
        </header>

        <div className="!mt-12 grid grid-cols-1 !gap-10 lg:grid-cols-12 xl:gap-12">
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="sticky !top-24 rounded-2xl border border-border-primary bg-surface-primary !p-6 shadow-sm md:p-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink">Refund Sections</p>
              <nav className="!mt-5 grid !gap-2.5">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setExpandedItem(section.id)}
                    className={`group flex items-center justify-between rounded-xl border !px-4 !py-3 text-sm transition-all duration-300 ${
                      activeSection === section.id
                        ? "border-brand/30 bg-surface-primary  text-brand"
                        : "border-transparent text-ink-2 hover:border-border hover:bg-bg hover:text-ink"
                    }`}
                  >
                    <span className="font-medium text-ink-2">
                      {String(index + 1).padStart(2, "0")}. {section.title}
                    </span>
                    <span className={`h-2 w-2 rounded-full ${activeSection === section.id ? "bg-brand" : "bg-border"}`} />
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <section className="!space-y-8 lg:col-span-8 xl:col-span-9">
            {sections.map((section, index) => {
              const isExpanded = expandedItem === section.id;
              return (
                <article
                  key={section.id}
                  id={section.id}
                  className={`scroll-mt-28 overflow-hidden rounded-2xl border bg-surface transition-all duration-300 ${
                    activeSection === section.id ? "border-brand/30 shadow-md" : "border-border-primary shadow-sm hover:shadow-md"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleAccordion(section.id)}
                    className={`flex !w-full items-start justify-between !gap-6 !px-7 !py-7 text-left md:px-10! md:py-8! cursor-pointer ${
                      activeSection === section.id ? "bg-surface-primary" : "bg-background-primary"
                    }`}
                    aria-expanded={isExpanded}
                    aria-controls={`panel-${section.id}`}
                  >
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
                        {section.eyebrow} / {String(index).padStart(2, "0")}
                      </p>
                      <h2 className="!mt-3 inline-flex rounded-md 2 px-2.5 py-1 font-display text-lg font-bold tracking-tight text-ink md:text-3xl">{section.title}</h2>
                    </div>
                    <div
                      className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-lg font-semibold transition-all duration-300 ${
                        isExpanded ? "border-brand/30 bg-brand-dim text-brand" : "border-border bg-bg text-ink-2"
                      }`}
                    >
                      {isExpanded ? "-" : "+"}
                    </div>
                  </button>

                  <div id={`panel-${section.id}`} className={`grid transition-all duration-400 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="border-t border-border-primary bg-surface-primary  px-7! pb-7! pt-6! md:px-10 md:pb-10! md:pt-7!">
                        <ul className="!space-y-4">
                          {section.content.map((line, i) => (
                            <li key={`${section.id}-${i}`} className="flex !gap-3 rounded-lg !px-3 !py-2 text-ink-2 transition-colors duration-200 hover:bg-bg">
                              <span className="!mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/80" />
                              <span className="leading-relaxed">{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            <div className="rounded-2xl border border-border-primary bg-surface-primary  !p-7 md:p-10!">
              <h3 className="font-display text-2xl font-bold text-ink">Important Legal Note</h3>
              <p className="!mt-2 text-sm leading-relaxed text-ink-2 md:text-base">
                Clear Privacy and Refund policies are essential legal documents that protect both the Company and its customers while improving transparency and trust.
              </p>
              <p className="!mt-5 rounded-lg border border-border-primary bg-surface-primary  !px-5 !py-4 text-sm text-ink-2 shadow-sm md:text-base">
                Customers should review these terms before purchasing, so they understand data handling, return conditions, and refund expectations in advance.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default RefundPolicyOrganism;
